import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Box, Typography, Paper, Divider, Button, Alert, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TextField } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { BACKEND_URL } from '../utils/constants';
import { useUser, useRole, PlanTier } from '../context/UserContext';
import { useLocation } from 'react-router-dom';
import PipelineTagSettings from '../components/PipelineTagSettings';
import EmbeddedCheckoutForm, { stripePromise } from '../components/EmbeddedCheckoutForm';
import { extractApiError } from '../utils/apiError';


import { ConstructionLoader } from '../components/ConstructionLoader';
const formatEpoch = (epoch?: number | null) => {
  if (!epoch) return '';
  try {
    return new Date(epoch * 1000).toLocaleDateString();
  } catch {
    return '';
  }
};

const formatMoney = (amount?: number | null, currency?: string | null, interval?: string | null) => {
  if (!amount) return '';
  const money = (amount / 100).toLocaleString(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() });
  return interval ? `${money} / ${interval}` : money;
};

const Settings = () => {
  const { isLoading, getAccessTokenSilently } = useAuth0();
  const { user: appUser, isAuthenticated, refreshUser } = useUser();
  const location = useLocation();
  const pricingRef = React.useRef<HTMLDivElement>(null);
  const paymentRef = React.useRef<HTMLDivElement>(null);

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [storedReferral, setStoredReferral] = React.useState<string | null>(null);
  const [showPayment, setShowPayment] = React.useState(false);
  const [message, setMessage] = React.useState<string>("");
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  const [subJustStarted, setSubJustStarted] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState<PlanTier>("pro");
  const [highlightedTier, setHighlightedTier] = React.useState<string | null>(null);

  const [subDetails, setSubDetails] = React.useState<any | null>(null);
  const [subLoading, setSubLoading] = React.useState(true);

  const [companyInfo, setCompanyInfo] = React.useState<any | null>(null);
  const [companyLoading, setCompanyLoading] = React.useState<boolean>(true);
  const [companyEditing, setCompanyEditing] = React.useState<boolean>(false);
  const [companyDraft, setCompanyDraft] = React.useState<{ company_name?: string; company_email?: string; company_phone_number?: string; company_logo_url?: string }>({});
  // Shown under the logo picker when the server refuses an upload (type, size, rate limit).
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const [companySaving, setCompanySaving] = React.useState<boolean>(false);
  // Enterprise batch 8: in a firm the company details are the firm's, and
  // only its admins change them (the server says which via can_edit).
  const [companyCanEdit, setCompanyCanEdit] = React.useState<boolean>(true);
  const [firmBranding, setFirmBranding] = React.useState<boolean>(false);
  const [companyError, setCompanyError] = React.useState<string | null>(null);
  const { isViewer } = useRole();

  const status: string | null = (appUser?.subscription_status ?? null) as any;
  const currentPeriodEnd: number | null = (appUser?.current_period_end ?? null) as any;
  const stripeCustomerId: string | null = (appUser?.stripe_customer_id ?? null) as any;

  const fetchSubscription = React.useCallback(async () => {
    try {
      setSubLoading(true);
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const r = await fetch(`${BACKEND_URL}/api/billing/subscription`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (!r.ok) {
        setSubDetails(null);
        return;
      }
      const d = await r.json();
      setSubDetails(d);
    } catch {
      setSubDetails(null);
    } finally {
      setSubLoading(false);
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    // Always attempt to fetch subscription; backend can resolve by user regardless
    fetchSubscription();
  }, [stripeCustomerId, fetchSubscription]);

  // If coming from promo flow, temporarily hide the "Start trial" button until fetch completes
  React.useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get('from') === 'promo') {
      setSubJustStarted(true);
      fetchSubscription();
    }
    const upgrade = qs.get('upgrade');
    if (upgrade && ['pro', 'max'].includes(upgrade.toLowerCase())) {
      const tier = upgrade.toLowerCase();
      setHighlightedTier(tier);
      setSelectedTier(tier as PlanTier);
      
      setTimeout(() => {
        pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [location.search, fetchSubscription]);

  // Fetch Company Info
  React.useEffect(() => {
    const run = async () => {
      if (!appUser?.email) {
        setCompanyInfo(null);
        setCompanyLoading(false);
        return;
      }
      try {
        setCompanyLoading(true);
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
        });
        const r = await fetch(`${BACKEND_URL}/api/company_info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          },
          credentials: 'include'
        });
        if (!r.ok) {
          setCompanyInfo(null);
          return;
        }
        const d = await r.json();
        setCompanyCanEdit(d?.can_edit !== false);
        setFirmBranding(Boolean(d?.firm_branding));
        if (d && d.exists) {
          setCompanyInfo(d);
          setCompanyDraft({
            company_name: d.company_name || '',
            company_email: d.company_email || '',
            company_phone_number: d.company_phone_number || '',
            company_logo_url: d.company_logo_url || ''
          });
        } else {
          setCompanyInfo(null);
          setCompanyDraft({
            company_name: '',
            company_email: '',
            company_phone_number: '',
            company_logo_url: ''
          });
        }
      } catch {
        setCompanyInfo(null);
      } finally {
        setCompanyLoading(false);
      }
    };
    run();
  }, [appUser?.email, getAccessTokenSilently]);

  const beginSignup = async (tier?: PlanTier) => {
    const targetTier = tier || selectedTier;
    if (tier) setSelectedTier(tier);
    setMessage("");
    setSuccessMessage("");
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const r = await fetch(`${BACKEND_URL}/api/billing/setup-intent`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    });
    if (!r.ok) {
      setMessage(await extractApiError(r, 'Could not initialize payment.'));
      return;
    }
    const d = await r.json();
    // Prefill the referral field with whatever code already owns this user, so
    // someone a rep sourced doesn't have to remember it a second time. Still
    // editable, and write-once on the backend means it can't be reassigned.
    try {
      const rr = await fetch(`${BACKEND_URL}/api/billing/referral`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (rr.ok) setStoredReferral((await rr.json()).referral_code || null);
    } catch {
      /* prefill is a convenience; never block checkout on it */
    }
    setClientSecret(d.clientSecret);
    setShowPayment(true);
    setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleTierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTier(e.target.value as PlanTier);
    if (showPayment) {
      setShowPayment(false);
      setClientSecret(null);
    }
  };

  const openPortal = async () => {
    setMessage("");
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const r = await fetch(`${BACKEND_URL}/api/billing/portal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    });
    if (!r.ok) {
      setMessage(await extractApiError(r, 'Could not open billing portal.'));
      return;
    }
    const d = await r.json();
    window.location.href = d.url;
  };

  const canManage = !!stripeCustomerId || (status && status !== 'canceled');
  const canStartTrial = (!status || status === 'incomplete_expired') && !subJustStarted && (subDetails?.eligible_for_trial !== false);
  const effectiveStatus: string | null = (subDetails?.status || status || null) as any;
  const isCanceled = effectiveStatus === 'canceled';

  if (isLoading || subLoading || companyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <ConstructionLoader />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1000, width: '100%', mt: 4, backgroundColor: 'white', p: 2, borderRadius: 2 }}>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Manage your account settings and preferences.
        </Typography>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your personal details here.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{isAuthenticated ? (appUser?.email || 'Unknown') : 'Not signed in'}</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Company Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {firmBranding && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {companyCanEdit
                ? 'These details and the logo brand the PDF exports of everyone in your firm.'
                : "These details and the logo brand your firm's PDF exports. Only your firm's admins can change them."}
            </Typography>
          )}
          {companyError && (
            <Alert severity="error" onClose={() => setCompanyError(null)} sx={{ mb: 2 }}>{companyError}</Alert>
          )}
          {companyLoading ? (
            <Typography variant="body2" color="text.secondary">Loading company information…</Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                {!companyEditing ? (
                  companyCanEdit ? (
                    <Button variant="outlined" size="small" onClick={() => setCompanyEditing(true)}>Edit</Button>
                  ) : null
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={companySaving}
                      onClick={async () => {
                        try {
                          setCompanySaving(true);
                          const token = await getAccessTokenSilently({
                            authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
                          });
                          const r = await fetch(`${BACKEND_URL}/api/company_info`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`
                            },
                            credentials: 'include',
                            body: JSON.stringify(companyDraft)
                          });
                          if (!r.ok) {
                            setCompanyError(await extractApiError(r, 'Could not save the company details.'));
                          }
                          if (r.ok) {
                            setCompanyError(null);
                            setCompanyEditing(false);
                            // refresh
                            const refreshed = await fetch(`${BACKEND_URL}/api/company_info`, {
                              method: 'GET',
                              headers: {
                                Authorization: `Bearer ${token}`
                              },
                              credentials: 'include'
                            });
                            const d = await refreshed.json();
                            setCompanyInfo(d && d.exists ? d : null);
                          }
                        } finally {
                          setCompanySaving(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setCompanyEditing(false);
                        setCompanyDraft({
                          company_name: companyInfo?.company_name || '',
                          company_email: companyInfo?.company_email || '',
                          company_phone_number: companyInfo?.company_phone_number || '',
                          company_logo_url: companyInfo?.company_logo_url || ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '220px 1fr', rowGap: 1.25, columnGap: 2 }}>
                <Typography variant="body2" color="text.secondary">Company Name</Typography>
                {companyEditing ? (
                  <input
                    value={companyDraft.company_name || ''}
                    onChange={(e) => setCompanyDraft((s) => ({ ...s, company_name: e.target.value }))}
                    style={{ width: '100%', padding: 8, border: '1px solid #e0e0e0', borderRadius: 6 }}
                  />
                ) : (
                  <Typography variant="body1">{companyInfo?.company_name || '—'}</Typography>
                )}

                <Typography variant="body2" color="text.secondary">Company Email</Typography>
                {companyEditing ? (
                  <input
                    value={companyDraft.company_email || ''}
                    onChange={(e) => setCompanyDraft((s) => ({ ...s, company_email: e.target.value }))}
                    style={{ width: '100%', padding: 8, border: '1px solid #e0e0e0', borderRadius: 6 }}
                  />
                ) : (
                  <Typography variant="body1">{companyInfo?.company_email || '—'}</Typography>
                )}

                <Typography variant="body2" color="text.secondary">Company Phone Number</Typography>
                {companyEditing ? (
                  <input
                    value={companyDraft.company_phone_number || ''}
                    onChange={(e) => setCompanyDraft((s) => ({ ...s, company_phone_number: e.target.value }))}
                    style={{ width: '100%', padding: 8, border: '1px solid #e0e0e0', borderRadius: 6 }}
                  />
                ) : (
                  <Typography variant="body1">{companyInfo?.company_phone_number || '—'}</Typography>
                )}

                <Typography variant="body2" color="text.secondary">Company Logo</Typography>
                <Box>
                  {companyInfo?.company_logo_url ? (
                    <Box sx={{ mb: 1 }}>
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img src={companyInfo.company_logo_url} style={{ maxHeight: 56, maxWidth: 240, objectFit: 'contain' }} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No logo uploaded</Typography>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    disabled={!companyEditing}
                    onChange={async (e) => {
                      const inputEl = e.currentTarget as HTMLInputElement;
                      const file = inputEl.files?.[0];
                      if (!file) return;
                      if (file && !['image/jpeg', 'image/png'].includes(file.type)) {
                        setLogoError('Only JPEG or PNG images can be uploaded.');
                        if (inputEl) inputEl.value = '';
                        return;
                      }
                      setLogoError(null);
                      try {
                        const token = await getAccessTokenSilently({
                          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
                        });
                        const fd = new FormData();
                        fd.append('file', file);
                        const r = await fetch(`${BACKEND_URL}/api/company_logo/upload`, {
                          method: 'POST',
                          headers: {
                            Authorization: `Bearer ${token}`
                          },
                          credentials: 'include',
                          body: fd
                        });
                        if (r.ok) {
                          const d = await r.json();
                          setCompanyInfo((s: any | null) => ({ ...(s || {}), company_logo_url: d.url, exists: true }));
                          setCompanyDraft((s: any) => ({ ...(s || {}), company_logo_url: d.url }));
                        } else {
                          let d: any = {};
                          try { d = await r.json(); } catch {}
                          setLogoError(d?.error || `Upload failed (${r.status}).`);
                        }
                      } catch {
                        setLogoError('Upload failed. Please try again.');
                      } finally {
                        if (inputEl) inputEl.value = '';
                      }
                    }}
                  />
                  {logoError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{logoError}</Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Paper>

        {isViewer ? (
          <Box sx={{ position: 'relative' }}>
            <Alert severity="info" sx={{ mt: 4, mb: -2 }}>
              Your firm's pipeline stages and labels. Viewers can see them but not change them.
            </Alert>
            <Box sx={{ pointerEvents: 'none', opacity: 0.75 }} aria-disabled="true">
              <PipelineTagSettings />
            </Box>
          </Box>
        ) : (
          <PipelineTagSettings />
        )}

        <Box ref={pricingRef} sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Plan & Billing
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, mb: 4, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '25%', verticalAlign: 'bottom', pb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Compare Plans</Typography>
                  </TableCell>
                  {(['freemium', 'pro', 'max'] as PlanTier[]).map((tierId) => {
                    const isCurrent = (appUser?.plan_tier || 'freemium') === tierId;
                    const isPro = tierId === 'pro';
                    const isFreemium = tierId === 'freemium';
                    const priceText = isFreemium ? '$0' : isPro ? '$20' : '$50';
                    const tierName = tierId.charAt(0).toUpperCase() + tierId.slice(1);
                    const isDowngrade = (appUser?.plan_tier === 'max' && (tierId === 'pro' || tierId === 'freemium')) || 
                                       (appUser?.plan_tier === 'pro' && tierId === 'freemium');
                    const isWait = highlightedTier === tierId;

                    return (
                      <TableCell key={tierId} align="center" sx={{ width: '25%', verticalAlign: 'top', pt: 3, pb: 2, backgroundColor: highlightedTier === tierId ? 'rgba(25, 118, 210, 0.08)' : 'inherit', transition: 'background-color 0.3s ease' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, position: 'relative' }}>
                          {isCurrent && (
                            <Chip label="Current Plan" color="success" size="small" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                          )}
                          <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize', mt: !isCurrent ? 3.5 : 0 }}>
                            {tierName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>{priceText}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>/mo</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {
                [
                  { 
                    feature: 'Multifamily Model', 
                    free: '✨ Unlimited access', 
                    pro: '✨ Unlimited access', 
                    max: '✨ Unlimited access' 
                  },
                  { 
                    feature: 'Mixed-Use Model', 
                    free: '🔒 Unlock with Pro', 
                    pro: '🚀 Included', 
                    max: '🚀 Included' 
                  },
                  { 
                    feature: 'Industrial & Retail Model', 
                    free: '🔒 Unlock with Pro', 
                    pro: '🚀 Included', 
                    max: '🚀 Included' 
                  },
                  { 
                    feature: 'MF & Mixed-Use Development Suite', 
                    free: '🔒 Exclusive to Max', 
                    pro: '🔒 Upgrade to Max', 
                    max: '🌟 Premium Access' 
                  },
                  { 
                    feature: 'Excel Export', 
                    free: '🔒 Pro feature', 
                    pro: '📊 Export anytime', 
                    max: '📊 Export anytime' 
                  },
                  { 
                    feature: 'PDF Reports', 
                    free: '🔒 Pro feature', 
                    pro: '🧾 Download ready', 
                    max: '🧾 Download ready' 
                  }
                ]
                .map((row, idx) => (
                  <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{row.feature}</TableCell>
                    <TableCell align="center" sx={{ color: row.free.includes('🔒') ? 'text.secondary' : 'inherit', backgroundColor: highlightedTier === 'freemium' ? 'rgba(25, 118, 210, 0.08)' : 'inherit', transition: 'background-color 0.3s ease' }}>{row.free}</TableCell>
                    <TableCell align="center" sx={{ color: row.pro.includes('🔒') ? 'text.secondary' : 'inherit', backgroundColor: highlightedTier === 'pro' ? 'rgba(25, 118, 210, 0.08)' : 'inherit', transition: 'background-color 0.3s ease' }}>{row.pro}</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: highlightedTier === 'max' ? 'rgba(25, 118, 210, 0.08)' : 'inherit', transition: 'background-color 0.3s ease' }}>{row.max}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell />
                  {(['freemium', 'pro', 'max'] as PlanTier[]).map((tierId) => {
                    const isCurrent = (appUser?.plan_tier || 'freemium') === tierId;
                    const isPro = tierId === 'pro';
                    const isFreemium = tierId === 'freemium';
                    const isDowngrade = (appUser?.plan_tier === 'max' && (tierId === 'pro' || tierId === 'freemium')) || 
                                       (appUser?.plan_tier === 'pro' && tierId === 'freemium');
                    const isWait = highlightedTier === tierId;
                    // Highlight Pro for anyone who hasn't started a paid plan yet.
                    // Purely presentational — there is no trial to be eligible for.
                    const isRecommended = isPro && canStartTrial;
                    const tierName = tierId.charAt(0).toUpperCase() + tierId.slice(1);

                    return (
                      <TableCell key={tierId} align="center" sx={{ borderBottom: 0, pt: 3, pb: 4, backgroundColor: highlightedTier === tierId ? 'rgba(25, 118, 210, 0.08)' : 'inherit', transition: 'background-color 0.3s ease' }}>
                        <Box sx={{ mt: 1, width: '100%', maxWidth: 200, mx: 'auto' }}>
                          {isCurrent ? (
                            <Button variant="outlined" color="success" disabled fullWidth sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>
                              Current Plan
                            </Button>
                          ) : isDowngrade ? (
                            <Tooltip title="Downgrades are currently handled via support.">
                              <span>
                                <Button variant="outlined" disabled fullWidth sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>
                                  Downgrade
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Box>
                              <Button
                                variant={isWait || isRecommended ? 'contained' : 'outlined'}
                                onClick={() => beginSignup(tierId)}
                                fullWidth
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  py: 0.8,
                                  boxShadow: isWait ? 2 : 0
                                }}
                              >
                                {isFreemium ? 'Switch to Freemium' : `Upgrade to ${tierName}`}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>


        {((appUser?.plan_tier && appUser.plan_tier !== 'freemium') || showPayment) && (
          <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Subscription and Payment Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

          {/* Row-based summary */}
          <Box sx={{ mb: 2 }}>
            {subLoading ? (
              <Typography variant="body2" color="text.secondary">Loading subscription…</Typography>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                rowGap: 1.25,
                columnGap: 2
              }}>
                {subDetails?.status && (
                  <>
                    <Typography variant="body2" color="text.secondary">Tier</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{subDetails?.plan_tier || '—'}</Typography>

                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body1">{subDetails?.status || status || '—'}</Typography>
                  </>
                )}

                {(subDetails?.amount || subDetails?.currency || subDetails?.interval) && (
                  <>
                    <Typography variant="body2" color="text.secondary">Price</Typography>
                    <Typography variant="body1">{formatMoney(subDetails?.amount, subDetails?.currency, subDetails?.interval)}</Typography>
                  </>
                )}

                {!!subDetails?.start_date && (
                  <>
                    <Typography variant="body2" color="text.secondary">Started</Typography>
                    <Typography variant="body1">{formatEpoch(subDetails.start_date)}</Typography>
                  </>
                )}

                {(subDetails?.trial_start || subDetails?.trial_end) && (
                  <>
                    <Typography variant="body2" color="text.secondary">Trial</Typography>
                    <Typography variant="body1">{`${formatEpoch(subDetails?.trial_start)} — ${formatEpoch(subDetails?.trial_end)}`}</Typography>
                  </>
                )}

                {(subDetails?.current_period_start || subDetails?.current_period_end) && (
                  <>
                    <Typography variant="body2" color="text.secondary">Current period</Typography>
                    <Typography variant="body1">{`${formatEpoch(subDetails?.current_period_start)} — ${formatEpoch(subDetails?.current_period_end)}`}</Typography>
                  </>
                )}

                {!!currentPeriodEnd && !subDetails?.current_period_end && (
                  <>
                    <Typography variant="body2" color="text.secondary">Renews</Typography>
                    <Typography variant="body1">{formatEpoch(currentPeriodEnd)}</Typography>
                  </>
                )}

                {typeof subDetails?.cancel_at_period_end === 'boolean' && (
                  <>
                    <Typography variant="body2" color="text.secondary">Cancel at period end</Typography>
                    <Typography variant="body1">{subDetails.cancel_at_period_end ? 'Yes' : 'No'}</Typography>
                  </>
                )}

                {(subDetails?.payment_method_brand || subDetails?.payment_method_last4) && (
                  <>
                    <Typography variant="body2" color="text.secondary">Payment method</Typography>
                    <Typography variant="body1">{`${subDetails?.payment_method_brand || ''} ${subDetails?.payment_method_last4 ? '• • • • ' + subDetails.payment_method_last4 : ''}`.trim()}</Typography>
                  </>
                )}

                {(subDetails?.hosted_invoice_url || subDetails?.invoice_pdf) && (
                  <>
                    <Typography variant="body2" color="text.secondary">Latest invoice</Typography>
                    <Typography variant="body1">
                      {subDetails?.hosted_invoice_url ? (
                        <a href={subDetails.hosted_invoice_url} target="_blank" rel="noreferrer">View invoice</a>
                      ) : subDetails?.invoice_pdf ? (
                        <a href={subDetails.invoice_pdf} target="_blank" rel="noreferrer">Download PDF</a>
                      ) : '—'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
          {message && <Alert severity="warning" sx={{ mb: 2 }}>{message}</Alert>}

          {/* Actions */}
          {!showPayment && !isLoading && !subLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(canStartTrial || isCanceled) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <select
                    value={selectedTier}
                    onChange={handleTierChange}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '1rem' }}
                  >
                    <option value="freemium">Freemium ($0/mo)</option>
                    <option value="pro">Pro ($20/mo)</option>
                    <option value="max">Max ($50/mo)</option>
                  </select>
                  <Button variant="contained" onClick={() => beginSignup()}>
                    {isCanceled ? 'Restart subscription' : 'Start subscription'}
                  </Button>
                </Box>
              )}
              {(canManage || subJustStarted) && (
                <Button variant="outlined" onClick={openPortal} sx={{ alignSelf: 'flex-start' }}>
                  Manage billing
                </Button>
              )}
            </Box>
          )}

          {showPayment && clientSecret && (
            <Box ref={paymentRef} sx={{ mt: 2, maxWidth: 600 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Checkout for {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Tier
                </Typography>
                <Button size="small" onClick={() => setShowPayment(false)}>Cancel</Button>
              </Box>
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckoutForm
                  tier={selectedTier}
                  initialPromoCode={storedReferral}
                  onSuccess={() => {
                    setShowPayment(false);
                    setSuccessMessage('Successfully upgraded! Your plan has been updated.');
                    setSubJustStarted(true);
                    fetchSubscription();
                  }}
                />
              </Elements>
            </Box>
          )}
        </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Settings;
